import { useMemo, useState } from "react";
import type { ApprovalRequest, BulkApprovalResponse } from "@/api/approvalRequests.service";
import { useBulkApprove, useBulkReject, useBulkSuspend } from "@/hooks/useApprovalActions";
import {
  describeRow,
  type BulkResultAction,
  type BulkResultRow,
} from "@/components/Modal/BulkResultModal";
import { canApproveOrReject, canSuspend, getDisplayContent, isSelectable } from "./utils";

export type BulkResultState = {
  isOpen: boolean;
  action: BulkResultAction;
  updatedCount: number;
  lockedIds: string[];
  notFoundIds: string[];
  invalidStatusIds: string[];
  alreadyProcessedIds: string[];
  callbackFailedIds: string[];
  rows: BulkResultRow[];
};

const CLOSED_RESULT: BulkResultState = {
  isOpen: false,
  action: "approve",
  updatedCount: 0,
  lockedIds: [],
  notFoundIds: [],
  invalidStatusIds: [],
  alreadyProcessedIds: [],
  callbackFailedIds: [],
  rows: [],
};

const QUALIFIES: Record<BulkResultAction, (status: string) => boolean> = {
  approve: canApproveOrReject,
  reject: canApproveOrReject,
  suspend: canSuspend,
};

const FAILURE_FALLBACK: Record<BulkResultAction, string> = {
  approve: "Failed to approve requests",
  reject: "Failed to reject requests",
  suspend: "Failed to suspend requests",
};

export function toBulkResultRows(items: ApprovalRequest[]): BulkResultRow[] {
  return items.map(item => ({
    id: item.id,
    firmName: item.firmName,
    firmRef: item.firmRef,
    lawyerName: item.lawyerName,
    contentOutput: getDisplayContent(item) || null,
  }));
}

function alreadyProcessedIds(action: BulkResultAction, result: BulkApprovalResponse): string[] {
  switch (action) {
    case "approve":
      return result.alreadyApprovedIds ?? [];
    case "reject":
      return result.alreadyRejectedIds ?? [];
    case "suspend":
      return result.alreadySuspendedIds ?? [];
  }
}

type UseBulkDecisionParams = {
  approvalRequests: ApprovalRequest[];
  selectedRows: Set<string>;
  editingRows: Set<string>;
  refetch: () => Promise<unknown>;
  clearSelection: () => void;
  onError: (message: string) => void;
};

export function useBulkDecision({
  approvalRequests,
  selectedRows,
  editingRows,
  refetch,
  clearSelection,
  onError,
}: UseBulkDecisionParams) {
  const [result, setResult] = useState<BulkResultState>(CLOSED_RESULT);
  const [running, setRunning] = useState<BulkResultAction | null>(null);
  const { mutate: bulkApprove } = useBulkApprove();
  const { mutate: bulkReject } = useBulkReject();
  const { mutate: bulkSuspend } = useBulkSuspend();
  const mutations: Record<BulkResultAction, (ids: string[]) => Promise<BulkApprovalResponse>> = {
    approve: bulkApprove,
    reject: bulkReject,
    suspend: bulkSuspend,
  };

  const selectedOnScreen = useMemo(
    () => approvalRequests.filter(item => selectedRows.has(item.id) && isSelectable(item.status)),
    [selectedRows, approvalRequests],
  );

  const idsByAction = useMemo(() => {
    const ids = { approve: [] as string[], reject: [] as string[], suspend: [] as string[] };
    for (const item of selectedOnScreen) {
      for (const action of Object.keys(ids) as BulkResultAction[]) {
        if (QUALIFIES[action](item.status)) ids[action].push(item.id);
      }
    }
    return ids;
  }, [selectedOnScreen]);

  const counts: Record<BulkResultAction, number> = {
    approve: idsByAction.approve.length,
    reject: idsByAction.reject.length,
    suspend: idsByAction.suspend.length,
  };

  const editingSelectedIds = useMemo(
    () => selectedOnScreen.filter(item => editingRows.has(item.id)).map(item => item.id),
    [selectedOnScreen, editingRows],
  );

  const blockedReason = useMemo((): string | null => {
    if (editingSelectedIds.length === 0) return null;
    if (editingSelectedIds.length > 1) {
      return `Save or cancel the edits on ${editingSelectedIds.length} selected rows first`;
    }
    const [id] = editingSelectedIds;
    const row = toBulkResultRows(approvalRequests).find(candidate => candidate.id === id);
    return `Save or cancel the edit on ${describeRow(id, row)} first`;
  }, [editingSelectedIds, approvalRequests]);

  const run = async (action: BulkResultAction) => {
    if (blockedReason) {
      onError(blockedReason);
      return;
    }
    const ids = idsByAction[action];
    if (ids.length === 0 || running) return;

    const rows = toBulkResultRows(approvalRequests);
    setRunning(action);
    try {
      const response = await mutations[action](ids);
      setResult({
        isOpen: true,
        action,
        updatedCount: response.updated.length,
        lockedIds: response.lockedIds,
        notFoundIds: response.notFoundIds,
        invalidStatusIds: response.invalidStatusIds,
        alreadyProcessedIds: alreadyProcessedIds(action, response),
        callbackFailedIds: response.callbackFailedIds ?? [],
        rows,
      });
      await refetch();
      clearSelection();
    } catch (error) {
      const message = error instanceof Error ? error.message : FAILURE_FALLBACK[action];
      onError(`Request failed: ${message}`);
    } finally {
      setRunning(null);
    }
  };

  return {
    selectedCount: selectedOnScreen.length,
    counts,
    blockedReason,
    running,
    run,
    result,
    closeResult: () => setResult(CLOSED_RESULT),
  };
}
